import {
    DestinationEvent,
    DestinationResponse,
    FormattedEvent,
    FormattedEventResponse,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { sdkRequest, translateServerErrors } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { getNavigatorLastRecentSearchTerms } from '@eventbrite/personalization';
import isEmpty from 'lodash/isEmpty';
import { EventBasicInformation } from '../../contexts/EventContext';
import { searchEventRelatedPromotedEvents } from '../_shared/searchEventRelatedPromotedEvents';

export interface PaginationResponse {
    object_count: number;
    page_number: number;
    page_size: number;
    page_count: number;
    has_more_items: boolean;
}

export interface DateInfo {
    timezone: string;
    local: string;
    utc: string;
}

interface TicketPrice {
    currency: string;
    major_value: string;
    value: number;
    display: string;
}

interface OrganizerEventResponse {
    name: {
        text: string;
        html: string;
    };
    description?: {
        text: string;
        html: string;
    };
    url: string;
    vanity_url?: string;
    start: DateInfo;
    end: DateInfo;
    organization_id: string;
    created: string;
    changed: string;
    published: string;
    capacity: number;
    capacity_is_custom: boolean;
    status: string;
    currency: string;
    listed: boolean;
    shareable: boolean;
    invite_only: boolean;
    online_event: boolean;
    show_remaining: boolean;
    tx_time_limit: number;
    hide_start_date: boolean;
    hide_end_date: boolean;
    locale: string;
    is_locked: boolean;
    privacy_setting: string;
    is_series: boolean;
    is_series_parent: boolean;
    inventory_type: string;
    is_reserved_seating: boolean;
    show_pick_a_seat: boolean;
    show_seatmap_thumbnail: boolean;
    show_colors_in_seatmap_thumbnail: boolean;
    source: string;
    is_free: boolean;
    version: string | null;
    summary: string;
    logo_id: string;
    organizer_id: string;
    venue_id: string;
    category_id: string | null;
    subcategory_id: string | null;
    format_id: string | null;
    id: string;
    resource_uri: string;
    is_externally_ticketed: boolean;
    logo: {
        crop_mask: {
            top_left: {
                x: number;
                y: number;
            };
            width: number;
            height: number;
        };
        original: {
            url: string;
            width: number | null;
            height: number | null;
        };
        id: string;
        url: string;
        aspect_ratio: string;
        edge_color: string | null;
        edge_color_set: boolean;
    };
    ticket_availability: {
        has_available_tickets: boolean;
        minimum_ticket_price: TicketPrice;
        maximum_ticket_price: TicketPrice;
        is_sold_out: boolean;
        start_sales_date: {
            timezone: string;
            local: string;
            utc: string;
        };
        waitlist_available: boolean;
    };
}

interface OrganizerEventsResponse {
    events: Array<OrganizerEventResponse>;
    pagination: PaginationResponse;
}

type RelatedEventsApiResponse = {
    organic_events: DestinationEvent[];
    promoted_events: DestinationEvent[];
};

const STANDARD_SERVER_ERROR = gettext('An error occured.');

const GENERAL_ERROR_SPEC = {
    default: () => STANDARD_SERVER_ERROR,
};

export const _getOrganizerEventsApiUrl = (organizerId: string): string =>
    `/organizers/${organizerId}/events/?expand=ticket_availability&status=live&only_public=true`;

export const _getOrganizerDestinationEventsApiUrl = (
    eventIds: string,
): string =>
    `/destination/events/?event_ids=${eventIds}&expand=event_sales_status,ticket_availability,image,primary_venue,saves,primary_organizer&include_parent_events=true`;

export const excludeDuplicatedEvents = (
    events: FormattedEvent[],
    eventIdsToExclude: string[],
) => events.filter((event) => !eventIdsToExclude.includes(event.id));

export const getRelatedEvents = async (
    isAuthenticated: boolean,
    event: EventBasicInformation,
): Promise<FormattedEventResponse> => {
    const eventId = event?.id.toString() || '';

    return getRelatedEventsAndPromoted(isAuthenticated, event)
        .then(({ organic_events, promoted_events }) => {
            const events = organic_events.filter(
                (event: DestinationEvent) => event.id !== eventId,
            );

            return {
                events: events.map(transformDestinationEvent),
                promotedEvents: promoted_events.map(transformDestinationEvent),
            };
        })
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};

async function getRelatedEventsAndPromoted(
    isAuthenticated: boolean,
    event: EventBasicInformation,
): Promise<RelatedEventsApiResponse> {
    const [organicEventsSearchResponse, promotedEventsSearchResponse] =
        await Promise.allSettled([
            fetchRelatedEvents(isAuthenticated, event),
            searchEventRelatedPromotedEvents(event),
        ]);

    if (organicEventsSearchResponse.status === 'rejected') {
        throw new Error(organicEventsSearchResponse.reason);
    }

    return {
        organic_events: organicEventsSearchResponse.value.organic_events,
        promoted_events:
            promotedEventsSearchResponse.status === 'fulfilled'
                ? promotedEventsSearchResponse.value
                : [],
    };
}

function _getUserStatsFromLocalStorage(isAuthenticated: boolean) {
    const formattedPreviousSearches = getNavigatorLastRecentSearchTerms(5);

    const views =
        typeof window !== 'undefined'
            ? window.localStorage.getItem('user_recent_views')
            : '';

    const userStatsData = {
        user_recent_searches: formattedPreviousSearches,
        user_recent_views: views || '',
        logged_in: isAuthenticated || false,
        browse_surface: 'listings',
    };

    return {
        method: 'POST',
        body: JSON.stringify(userStatsData),
    };
}

export async function fetchRelatedEvents(
    isAuthenticated: boolean,
    event: EventBasicInformation,
): Promise<RelatedEventsApiResponse> {
    const response = await sdkRequest(
        `/destination/events/${event.id}/related/?expand=image,primary_venue,ticket_availability,saves,primary_organizer`,
        _getUserStatsFromLocalStorage(isAuthenticated),
    );

    return {
        organic_events: response.events,
        promoted_events: response.promoted_events,
    };
}

export const getOrganizerEvents = (
    organizerId: string,
): Promise<OrganizerEventsResponse> =>
    sdkRequest(_getOrganizerEventsApiUrl(organizerId))
        .then((response: OrganizerEventsResponse) => response)
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));

export const getOpenSaleEvents = (
    events: OrganizerEventResponse[],
): OrganizerEventResponse[] =>
    events?.filter(
        (event: OrganizerEventResponse) =>
            event.ticket_availability?.has_available_tickets,
    );

export const filterEvents = (
    amountOfEvents: number,
    events: any[],
    eventOwnerId: string,
) => {
    const eventsWithoutOwner = events?.filter((event: any) => {
        return event.id !== eventOwnerId;
    });
    return eventsWithoutOwner?.slice(0, amountOfEvents);
};

export const NUMBER_OF_MORE_EVENTS_TO_SHOW = 3;

export const getMoreEventsFromThisOrganizerFiltered = (
    eventOwnerId: string,
    events: OrganizerEventResponse[],
): OrganizerEventResponse[] => {
    const openSaleEvents = getOpenSaleEvents(events);
    const filteredEvents = filterEvents(
        NUMBER_OF_MORE_EVENTS_TO_SHOW,
        openSaleEvents,
        eventOwnerId,
    );

    return filteredEvents;
};

export const getOrganizerRelatedEventsFromDestination = async (
    organizerId: string,
    eventId: string,
): Promise<FormattedEventResponse | undefined> => {
    const { events } = await getOrganizerEvents(organizerId);

    const filteredEvents = getMoreEventsFromThisOrganizerFiltered(
        eventId,
        events,
    );
    const relatedEventIds = filteredEvents.map((event) => event.id);

    if (isEmpty(relatedEventIds)) {
        return;
    }

    return sdkRequest(
        _getOrganizerDestinationEventsApiUrl(relatedEventIds.join()),
    )
        .then((response: DestinationResponse) => ({
            events: response.events.map(transformDestinationEvent),
        }))
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};
